import { useRouterState } from "@tanstack/react-router"
import { ButtonLink } from "design-system/atoms/Button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "design-system/atoms/Collapsible"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "design-system/organisms/Sidebar"
import { cn } from "design-system/utils"
import { CornerDownRight } from "lucide-react"
import React, { useLayoutEffect, useRef } from "react"
import { NavLink } from "./Sidebar"

type Props = {
  items: NavLink[]
  className?: string
}

function useVerticalLine(
  pathname: string,
  items: NavLink[],
  refs: {
    parent: Record<string, HTMLElement | null>
    child: Record<string, HTMLElement | null>
    icon: Record<string, HTMLElement | null>
    container: React.RefObject<HTMLElement | null>
  },
) {
  const lineRef = useRef<HTMLDivElement>(null)

  const urlMapRef = useRef<Map<string, { parentKey: string; subKey: string }> | null>(null)
  if (!urlMapRef.current) {
    const m = new Map<string, { parentKey: string; subKey: string }>()
    items.forEach((item) =>
      item.subItems?.forEach((sub) => {
        if (sub.url) m.set(sub.url, { parentKey: item.title, subKey: sub.title })
      }),
    )
    urlMapRef.current = m
  }

  const prevParentRef = useRef<string | null>(null)

  useLayoutEffect(() => {
    const lineEl = lineRef.current
    if (!lineEl) return

    const urlMap = urlMapRef.current!
    let bestUrl = ""
    let activeItem: { parentKey: string; subKey: string } | null = null

    for (const [url, keys] of urlMap) {
      if (pathname.startsWith(url) && url.length > bestUrl.length) {
        bestUrl = url
        activeItem = keys
      }
    }

    if (!activeItem) {
      lineEl.style.opacity = "0"
      prevParentRef.current = null
      return
    }

    const newParent = activeItem.parentKey
    const prevParent = prevParentRef.current

    const parentEl = refs.parent[newParent]
    const childEl = refs.child[activeItem.subKey]
    const iconEl = refs.icon[newParent]
    const containerEl = refs.container.current
    if (!(parentEl && childEl && iconEl && containerEl)) return

    const p = parentEl.getBoundingClientRect()
    const c = childEl.getBoundingClientRect()
    const i = iconEl.getBoundingClientRect()
    const cont = containerEl.getBoundingClientRect()

    const iconOffset = iconEl.offsetLeft
    const iconWidth = iconEl.offsetWidth
    const left = iconOffset + Math.round(iconWidth / 2) - 1
    const top = Math.round(p.top - cont.top + p.height / 2 + i.height / 2 + 2)
    const height = Math.round(c.top - p.top - i.height)

    const animate = () => {
      requestAnimationFrame(() => {
        lineEl.style.transition = ""
        lineEl.style.opacity = "1"
        lineEl.style.transform = `translateX(${left}px) translateY(${top}px)`
        lineEl.style.height = `${height}px`
      })
    }

    if (prevParent !== newParent) {
      lineEl.style.transition = "none"
      lineEl.style.opacity = "0"
      lineEl.style.transform = `translateX(${left}px) translateY(${top}px)`
      lineEl.style.height = `${height}px`
      void lineEl.offsetHeight
    }
    animate()

    prevParentRef.current = newParent
  }, [pathname, refs])

  return lineRef
}

export function NavMain({ items, className }: Props) {
  const router = useRouterState()
  const { isMobile, setOpenMobile, state } = useSidebar()
  const isCollapsed = state === "collapsed"
  const parentRefs = useRef<Record<string, HTMLElement | null>>({})
  const childRefs = useRef<Record<string, HTMLElement | null>>({})
  const iconRefs = useRef<Record<string, HTMLElement | null>>({})
  const containerRef = useRef<HTMLDivElement>(null)

  const lineRef = useVerticalLine(router.location.pathname, items, {
    parent: parentRefs.current,
    child: childRefs.current,
    icon: iconRefs.current,
    container: containerRef,
  })

  const closeMobileSidebar = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <SidebarGroup className={className}>
      <SidebarGroupContent ref={containerRef} className="flex flex-col gap-2 relative">
        {/* Siempre está en el DOM; controlamos visibilidad por CSS */}
        <div
          ref={lineRef}
          className="absolute w-px bg-muted-line z-50 transition-all duration-200 opacity-0 group-data-[collapsible=icon]:hidden"
        />

        <SidebarMenu>
          {items.map((item) => {
            const hasActiveChild = item.subItems?.some(
              (sub) => sub.url && router.location.pathname.startsWith(sub.url),
            )
            const inRoute =
              item.url && router.location.pathname.startsWith(item.url) && !hasActiveChild

            return (
              <Collapsible
                open={inRoute || hasActiveChild}
                className="group/collapsible relative"
                key={item.title}
              >
                <SidebarMenuItem>
                  <div
                    ref={(el) => {
                      parentRefs.current[item.title] = el
                    }}
                    className="relative z-10"
                  >
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.title} asChild className="px-5">
                        <ButtonLink
                          key={item.url}
                          onClick={(ev) => {
                            if (item.externalUrl) {
                              ev.preventDefault()
                              window.open(item.externalUrl)
                            }
                            closeMobileSidebar()
                          }}
                          to={item.url}
                          target={item.target}
                          variant="sidebar"
                          className={cn(
                            "h-10 font-normal gap-3 w-full justify-start hover:bg-primary/5",
                            inRoute && "bg-primary/5 font-medium",
                            hasActiveChild && "font-medium",
                          )}
                        >
                          {item.icon && (
                            <div
                              ref={(el) => {
                                iconRefs.current[item.title] = el
                              }}
                              className="flex items-center justify-center w-4 h-4"
                            >
                              <item.icon className="w-4 h-4 shrink-0" />
                            </div>
                          )}
                          <span className="transition-all duration-200 group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0 overflow-hidden">
                            {item.title}
                          </span>
                        </ButtonLink>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                  </div>

                  {item.subItems && (
                    <CollapsibleContent className="px-0">
                      {item.subItems.map((sub) => {
                        const inSubRoute = sub.url && router.location.pathname.startsWith(sub.url)

                        return (
                          <SidebarMenuSub key={sub.title} className="border-none p-0 pt-1 m-0">
                            <SidebarMenuSubItem className="group/subItem">
                              <div
                                ref={(el) => {
                                  childRefs.current[sub.title] = el
                                }}
                                className="relative z-10"
                              >
                                <SidebarMenuSubButton asChild tooltip={sub.title}>
                                  <ButtonLink
                                    key={sub.url}
                                    onClick={(ev) => {
                                      if (sub.externalUrl) {
                                        ev.preventDefault()
                                        window.open(sub.externalUrl)
                                      }
                                      closeMobileSidebar()
                                    }}
                                    to={sub.url}
                                    variant="sidebar"
                                    className={cn(
                                      "h-10 font-normal gap-3 w-full justify-start hover:bg-primary/5 text-muted-foreground",
                                      inSubRoute && "bg-primary/5 text-primary font-medium",
                                      !isCollapsed && "pl-[21px]",
                                    )}
                                  >
                                    <div className="flex items-center justify-center w-4 h-4 pl-0.5">
                                      {!isCollapsed || !("icon" in sub && sub.icon) ? (
                                        <CornerDownRight
                                          className={cn(
                                            "duration-150 opacity-0 group-hover/subItem:opacity-100 shrink-0 w-4 h-4 text-muted-line",
                                            inSubRoute && "opacity-100",
                                          )}
                                        />
                                      ) : (
                                        <div
                                          ref={(el) => {
                                            iconRefs.current[sub.title] = el
                                          }}
                                          className="flex items-center justify-center w-4 h-4 pl-6"
                                        >
                                          <sub.icon className="shrink-0 w-4 h-4 text-muted-foreground" />
                                        </div>
                                      )}
                                    </div>
                                    <span className="transition-all duration-200 max-w-[200px] group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0 overflow-hidden">
                                      {sub.title}
                                    </span>
                                  </ButtonLink>
                                </SidebarMenuSubButton>
                              </div>
                            </SidebarMenuSubItem>
                          </SidebarMenuSub>
                        )
                      })}
                    </CollapsibleContent>
                  )}
                </SidebarMenuItem>
              </Collapsible>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
